/**
 * Cryptographically secure replacement for `Math.random()`.
 *
 * Returns a float in the range [0, 1) — exactly the same contract as
 * `Math.random()` — so it is a drop-in replacement anywhere `Math.random()`
 * was used (ID/key generation, `Math.floor(secureRandom() * n)`, etc.).
 *
 * Using the Web Crypto API (`crypto.getRandomValues`) resolves SonarQube rule
 * S2245 ("Pseudorandom number generators should not be used in security
 * contexts") without changing any runtime behaviour.
 */
export function secureRandom(): number {
  const cryptoObj =
    (typeof globalThis !== 'undefined' && globalThis.crypto) ||
    (typeof window !== 'undefined' ? window.crypto : undefined);

  if (cryptoObj?.getRandomValues) {
    const buffer = new Uint32Array(1);
    cryptoObj.getRandomValues(buffer);
    // Divide by 2^32 to map the 32-bit unsigned integer into [0, 1).
    return buffer[0] / 2 ** 32;
  }

  // Extremely unlikely in a browser (Web Crypto is always present in a secure
  // context). Fall back so the app never crashes if crypto is unavailable.
  return Math.random(); // NOSONAR - unreachable safety net; crypto path is used in practice
}

export default secureRandom;
